'use client';

import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSDKStore } from '@/lib/sdk/store';
import { BarChart3, Activity, Clock, AlertCircle, TrendingUp } from 'lucide-react';

interface KeyUsageStatsProps {
  keyId: string;
  keyName: string;
  open: boolean;
  onClose: () => void;
}

export function KeyUsageStats({ keyId, keyName, open, onClose }: KeyUsageStatsProps) {
  const { usage, usageLoading, loadUsage } = useSDKStore();

  useEffect(() => {
    if (open && keyId) {
      loadUsage(keyId, 30);
    }
  }, [open, keyId, loadUsage]);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="size-5" />
            Usage Statistics
          </DialogTitle>
          <DialogDescription>
            API usage for "{keyName}" over the last 30 days
          </DialogDescription>
        </DialogHeader>

        {usageLoading ? (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
            <Skeleton className="h-[200px]" />
          </div>
        ) : usage ? (
          <div className="space-y-6 py-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <Activity className="size-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Total Requests</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {usage.total_requests.toLocaleString()}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="size-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Today</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {usage.requests_today.toLocaleString()}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <Clock className="size-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Avg Latency</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {usage.avg_latency_ms.toFixed(0)}ms
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="size-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Error Rate</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {(usage.error_rate * 100).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Usage Chart (simplified bar visualization) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Requests per Day</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-[150px]">
                  {usage.requests_by_day.slice(-14).map((day, i) => {
                    const maxRequests = Math.max(...usage.requests_by_day.map((d) => d.count));
                    const height = maxRequests > 0 ? (day.count / maxRequests) * 100 : 0;

                    return (
                      <div
                        key={day.date}
                        className="flex-1 group relative"
                        title={`${day.date}: ${day.count} requests`}
                      >
                        <div
                          className="bg-primary/80 hover:bg-primary rounded-t transition-all"
                          style={{ height: `${Math.max(height, 2)}%` }}
                        />
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 -mb-6 text-xs text-muted-foreground hidden group-hover:block whitespace-nowrap">
                          {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-8 text-xs text-muted-foreground">
                  <span>14 days ago</span>
                  <span>Today</span>
                </div>
              </CardContent>
            </Card>

            {/* Top Endpoints */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Top Endpoints</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {usage.top_endpoints.slice(0, 5).map((endpoint, i) => {
                    const percentage = (endpoint.count / usage.total_requests) * 100;
                    return (
                      <div key={endpoint.endpoint} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-mono text-xs truncate max-w-[300px]">
                            {endpoint.endpoint}
                          </span>
                          <Badge variant="secondary">
                            {endpoint.count.toLocaleString()} ({percentage.toFixed(1)}%)
                          </Badge>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {usage.top_endpoints.length === 0 && (
                    <p className="text-sm text-muted-foreground">No endpoint data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <BarChart3 className="size-12 mx-auto mb-4 opacity-50" />
            <p>No usage data available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
